/**
 * Client mirror of services/coms/src/routes/Forms/formFamily.ts.
 *
 * The same form number is a structurally different document depending on the
 * publishing authority. Callers name forms by a family-agnostic base id and the
 * incident's family decides which template is rendered.
 */

export const ICS_FORM_FAMILIES = ['uscg', 'fema'] as const;
export type IcsFormFamily = (typeof ICS_FORM_FAMILIES)[number];

export const DEFAULT_ICS_FORM_FAMILY: IcsFormFamily = 'uscg';

/** Families a new incident may be created with. Coast Guard stays the default. */
export const SELECTABLE_ICS_FORM_FAMILIES: readonly IcsFormFamily[] = [
  'uscg',
  'fema',
];

export const ICS_FORM_FAMILY_LABELS: Record<IcsFormFamily, string> = {
  uscg: 'U.S. Coast Guard',
  fema: 'FEMA',
};

export const ICS_FORM_FAMILY_DESCRIPTIONS: Record<IcsFormFamily, string> = {
  uscg: 'Coast Guard ICS forms, used for marine and MTSA-regulated response.',
  fema: 'FEMA/NIMS ICS forms, used for all-hazards domestic response.',
};

export function isIcsFormFamily(value: unknown): value is IcsFormFamily {
  return (
    typeof value === 'string' &&
    (ICS_FORM_FAMILIES as readonly string[]).includes(value)
  );
}

export function resolveIcsFormFamily(value: unknown): IcsFormFamily {
  return isIcsFormFamily(value) ? value : DEFAULT_ICS_FORM_FAMILY;
}

/** Reads the family off an incident, defaulting for incidents that predate it. */
export function resolveIncidentFormFamily(incident: unknown): IcsFormFamily {
  if (typeof incident !== 'object' || incident === null) {
    return DEFAULT_ICS_FORM_FAMILY;
  }
  const record = incident as Record<string, unknown>;
  return resolveIcsFormFamily(record.ics_form_family ?? record.icsFormFamily);
}

/**
 * FEMA publishes the unadorned NIMS forms, so its templates use the bare base
 * id.
 */
const FAMILY_SUFFIX: Record<IcsFormFamily, string> = {
  uscg: '_CG',
  fema: '',
};

/**
 * Names that do not follow the suffix rule. The Coast Guard 201 is registered
 * with a hyphen, and FEMA republishes some Coast Guard editions verbatim.
 */
const FAMILY_NAME_OVERRIDES: Partial<
  Record<IcsFormFamily, Record<string, string>>
> = {
  uscg: {
    ICS_201: 'ICS_201-CG',
    CG_5136: 'CG_5136',
    CG_5136X: 'CG_5136X',
  },
  fema: {
    ICS_230: 'ICS_230_CG',
    ICS_233: 'ICS_233_CG',
  },
};

/**
 * Coast Guard base ids with a committed official Word template or builder.
 * 202A, 202B, 215A and 238 stay PDF-only.
 */
export const USCG_WORD_TEMPLATE_FORMS = [
  'ICS_201',
  'ICS_202',
  'ICS_203',
  'ICS_204',
  'ICS_204A',
  'ICS_205',
  'ICS_205A',
  'ICS_206',
  'ICS_207',
  'ICS_208',
  'ICS_208HM',
  'ICS_209',
  'ICS_211',
  'ICS_211A',
  'ICS_213RR',
  'ICS_214',
  'ICS_215',
  'ICS_217A',
  'ICS_220',
  'ICS_220UAS',
  'ICS_221',
  'ICS_225',
  'ICS_230',
  'ICS_232',
  'ICS_232A',
  'ICS_233',
  'ICS_234',
  'ICS_235',
  'ICS_236',
  'ICS_261',
  'IAP_COVER',
] as const;

/** Base ids with a committed FEMA Word template on the server. */
export const FEMA_WORD_TEMPLATE_FORMS = [
  'ICS_201',
  'ICS_202',
  'ICS_203',
  'ICS_204',
  'ICS_205',
  'ICS_205A',
  'ICS_206',
  'ICS_207',
  'ICS_208',
  'ICS_208HM',
  'ICS_209',
  'ICS_210',
  'ICS_211',
  'ICS_213',
  'ICS_213RR',
  'ICS_214',
  'ICS_215',
  'ICS_215A',
  'ICS_217A',
  'ICS_218',
  'ICS_220',
  'ICS_221',
  'ICS_225',
  'ICS_260',
] as const;

export const FAMILY_IMPLEMENTED_FORMS: Record<
  IcsFormFamily,
  readonly string[] | 'all'
> = {
  uscg: 'all',
  // Every form with a FEMA Word template, plus 230 and 233, which come free
  // because FEMA republishes the Coast Guard editions verbatim.
  fema: [...FEMA_WORD_TEMPLATE_FORMS, 'ICS_230', 'ICS_233'],
};

export function isFormImplementedForFamily(
  baseId: string,
  family: IcsFormFamily
): boolean {
  const implemented = FAMILY_IMPLEMENTED_FORMS[family];
  return implemented === 'all' || implemented.includes(baseId);
}

/**
 * Base ids each family exports as Word.
 *
 * Coast Guard forms with a committed official template (or a dedicated
 * builder) appear here. 202A, 202B, 215A and 238 stay off the list.
 */
export const FAMILY_DOCX_FORMS: Record<IcsFormFamily, readonly string[]> = {
  uscg: USCG_WORD_TEMPLATE_FORMS,
  fema: FEMA_WORD_TEMPLATE_FORMS,
};

export function supportsDocxForFamily(
  baseId: string,
  family: IcsFormFamily
): boolean {
  return FAMILY_DOCX_FORMS[family].includes(baseId);
}

/** Strips a family suffix from a concrete form name, yielding its base id. */
export function toBaseFormId(formName: string): string {
  return formName.replace(/[_-](CG|FEMA|EPA)$/i, '');
}

export function resolveFormName(baseId: string, family: IcsFormFamily): string {
  const override = FAMILY_NAME_OVERRIDES[family]?.[baseId];
  if (override) return override;
  return `${baseId}${FAMILY_SUFFIX[family]}`;
}
